import { useState, useEffect, useRef, useCallback, useMemo, startTransition } from 'react';
import { SSCCProps } from '../../../datastore/ssccs';
import { ASNOccurrences, SSCCOccurrences } from '../utils/types';

const DEBOUNCE_MS = 300;

interface UseUnassignedSSCCsOptions {
  openStagedSsccById: Map<string, SSCCProps>;
  ssccOccurrences: SSCCOccurrences;
  asnOccurrences: ASNOccurrences;
  ssccSearch: string;
  selectedSSCCStatus: string;
  ssccNicknameSearch: string;
  asnSearch: string;
  selectedASNStatus: string;
  asnPoNumberSearch: string;
  showOnlyASNDuplicates: boolean;
}

export function useUnassignedSSCCs({
  openStagedSsccById,
  ssccOccurrences,
  asnOccurrences,
  ssccSearch,
  selectedSSCCStatus,
  ssccNicknameSearch,
  asnSearch,
  selectedASNStatus,
  asnPoNumberSearch,
  showOnlyASNDuplicates
}: UseUnassignedSSCCsOptions): SSCCProps[] {
  const [unassignedIds, setUnassignedIds] = useState<string[]>([]);
  const [workerReady, setWorkerReady] = useState(false);
  const workerRef = useRef<Worker | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        ssccSearch.trim() ||
        selectedSSCCStatus ||
        ssccNicknameSearch.trim() ||
        asnSearch.trim() ||
        selectedASNStatus ||
        asnPoNumberSearch.trim()
      ),
    [ssccSearch, selectedSSCCStatus, ssccNicknameSearch, asnSearch, selectedASNStatus, asnPoNumberSearch]
  );

  const canUseFastPath = !hasActiveFilters && !showOnlyASNDuplicates;

  const assignedSSCCIds = useMemo(() => Array.from(ssccOccurrences.keys()), [ssccOccurrences]);
  const assignedSSCCIdSet = useMemo(() => new Set(assignedSSCCIds), [assignedSSCCIds]);

  const asnDuplicateKeys = useMemo(
    () =>
      showOnlyASNDuplicates
        ? Array.from(asnOccurrences.entries())
            .filter(([, occurrences]) => occurrences.length > 1)
            .map(([asnKey]) => asnKey)
        : [],
    [showOnlyASNDuplicates, asnOccurrences]
  );

  const fastPathUnassignedIds = useMemo(() => {
    if (!canUseFastPath) {
      return [] as string[];
    }

    return Array.from(openStagedSsccById.keys())
      .filter((ssccId) => !assignedSSCCIdSet.has(ssccId))
      .sort((a, b) => b.localeCompare(a, undefined, { numeric: true, sensitivity: 'base' }));
  }, [canUseFastPath, openStagedSsccById, assignedSSCCIdSet]);

  useEffect(() => {
    try {
      const worker = new Worker(new URL('../workers/unassignedSsccsWorker.ts', import.meta.url));
      worker.onmessage = (e: MessageEvent) => {
        startTransition(() => {
          setUnassignedIds(e.data.ids);
        });
      };
      worker.onerror = (err) => {
        console.error('Unassigned SSCCs worker error:', err);
      };
      workerRef.current = worker;
      setWorkerReady(true);
    } catch (err) {
      console.error('Failed to create unassigned SSCCs worker:', err);
    }

    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const minimalSsccs = useMemo(
    () =>
      Array.from(openStagedSsccById.values()).map((sscc) => ({
        id: sscc.id,
        status: sscc.status,
        nickname: sscc.nickname,
        receiptedEvents: (sscc.receiptedEvents || []).map((event) => ({
          packages: (event.packages || []).map((pkg) => ({
            asn: pkg.asn,
            id: pkg.id,
            status: pkg.status,
            po: pkg.po
          }))
        }))
      })),
    [openStagedSsccById]
  );

  const postToWorker = useCallback(() => {
    if (!workerRef.current) return;

    workerRef.current.postMessage({
      ssccs: minimalSsccs,
      assignedSSCCIds,
      asnDuplicateKeys,
      ssccSearch,
      selectedSSCCStatus,
      ssccNicknameSearch,
      asnSearch,
      selectedASNStatus,
      asnPoNumberSearch,
      showOnlyASNDuplicates
    });
  }, [
    minimalSsccs,
    assignedSSCCIds,
    asnDuplicateKeys,
    ssccSearch,
    selectedSSCCStatus,
    ssccNicknameSearch,
    asnSearch,
    selectedASNStatus,
    asnPoNumberSearch,
    showOnlyASNDuplicates
  ]);

  useEffect(() => {
    if (!canUseFastPath) {
      return;
    }

    setUnassignedIds(fastPathUnassignedIds);
  }, [canUseFastPath, fastPathUnassignedIds]);

  useEffect(() => {
    if (!workerReady) return;
    if (canUseFastPath) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(postToWorker, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [postToWorker, workerReady, canUseFastPath]);

  return useMemo(
    () => unassignedIds.map((id) => openStagedSsccById.get(id)).filter((s): s is SSCCProps => Boolean(s)),
    [unassignedIds, openStagedSsccById]
  );
}
